/**
 * CredentialLifecycleService — manages OAuth token refresh outside the SecretsRouter.
 *
 * Works with opaque token blobs stored via the {@link SecretsRouter}; providers remain
 * OAuth-unaware. Phase 1 ships non-OAuth paths (always returns `not_applicable`).
 * Full proactive OAuth refresh with expiry checking ships in Phase 3.4.
 *
 * @docLink packages/secrets/concepts#credential-lifecycle
 */

import type { SecretsRouter } from "./router.js";

/**
 * Configuration for {@link CredentialLifecycleService}.
 *
 * Controls how aggressively the service proactively refreshes credentials before they expire.
 * The default lead time is 300 seconds (5 minutes).
 *
 * @docLink packages/secrets/concepts#credential-lifecycle
 */
export interface CredentialLifecycleConfig {
  /** How far before expiry to trigger a proactive refresh (seconds). */
  refreshLeadTimeSec: number;
}

/**
 * Result returned by {@link CredentialLifecycleService.refreshIfNeeded}.
 *
 * The `reason` discriminant indicates why a refresh did or did not occur.
 * An `error` string is present only when `reason` is `refresh_failed`.
 *
 * @docLink packages/secrets/concepts#credential-lifecycle
 */
export interface RefreshResult {
  refreshed: boolean;
  reason: "not_applicable" | "still_valid" | "refreshed" | "refresh_failed";
  error?: string;
}

export class CredentialLifecycleService {
  private router: SecretsRouter;

  // `_config` is accepted for signature stability but not stored yet: Phase 1
  // ships no OAuth refresh, so there is nothing to configure. Phase 3.4 will
  // store and read `refreshLeadTimeSec` when proactive refresh lands.
  constructor(router: SecretsRouter, _config?: Partial<CredentialLifecycleConfig>) {
    this.router = router;
  }

  /**
   * Attempt to refresh a credential if needed.
   * Phase 1: always returns not_applicable (no OAuth support yet).
   */
  async refreshIfNeeded(credentialRef: string): Promise<RefreshResult> {
    // Phase 1: no OAuth refresh implemented.
    // The credential exists and is valid as-is (static secrets, env vars, etc.)
    try {
      await this.router.getMetadata(credentialRef);
      return { refreshed: false, reason: "not_applicable" };
    } catch {
      return { refreshed: false, reason: "refresh_failed", error: "Credential not found" };
    }
  }

  /**
   * Check if a credential is still valid (not expired).
   * Phase 1: always returns true for non-OAuth credentials.
   */
  async isValid(credentialRef: string): Promise<boolean> {
    try {
      await this.router.getSecret(credentialRef);
      return true;
    } catch {
      return false;
    }
  }
}
